import { Capacitor } from '@capacitor/core';
import { otaUpdater } from './otaUpdater';
import { isIdle } from './otaActivity';
import { isOutsideBusinessHours } from './otaWindow';
import { isOtaEnabled, isOtaPolicyReady, subscribeOtaPolicyReady } from './otaSettings';

// Wires the OTA lifecycle into app startup. All functions are safe no-ops on
// web/desktop (non-native) and when the native plugins aren't present.

const DEFAULT_INTERVAL_MS = 30 * 60 * 1000; // check every ~30 min while running

const nativePlugin = (name) => {
  if (!Capacitor.isNativePlatform()) return null;
  return (typeof window !== 'undefined' && window.Capacitor?.Plugins?.[name]) || null;
};

// The rollback guarantee: tell capacitor-updater the current bundle booted far
// enough to be usable. If this is never called (white screen / crash on a bad
// bundle), the plugin auto-reverts to the previous bundle on the next launch.
// Call this as soon as the app shell has rendered.
export const notifyAppReady = async () => {
  const updater = nativePlugin('CapacitorUpdater');
  if (!updater?.notifyAppReady) return;
  try {
    await updater.notifyAppReady();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[ota] notifyAppReady failed', err);
  }
};

let stopScheduler = null;

// Begin idle-gated update checks: once now, on every app resume, and on an
// interval. Returns a stop() function. Idempotent — starting twice is a no-op.
export const startOtaUpdates = ({ intervalMs = DEFAULT_INTERVAL_MS, updater = otaUpdater } = {}) => {
  if (stopScheduler) return stopScheduler;
  if (!Capacitor.isNativePlatform() || !updater.isSupported()) return () => {};

  // A downloaded bundle is only applied when the POS is idle AND the store is
  // closed (outside business hours). Downloads still happen on any tick, so the
  // bundle is cached and ready to swap the moment both gates open.
  const canApply = () => isIdle() && isOutsideBusinessHours();

  const tick = () => {
    // Do nothing until the active store's OTA policy has actually been loaded —
    // otherwise the permissive startup defaults (enabled + no business hours)
    // could update during store hours or for an OTA-disabled store.
    if (!isOtaPolicyReady()) return;
    // Respect the per-store on/off switch: when OTA is disabled for the active
    // store, don't check, download, or apply anything. Read each tick so a
    // settings change takes effect as soon as the policy is refreshed.
    if (!isOtaEnabled()) return;
    Promise.resolve(updater.checkAndStage(canApply)).catch(() => {});
  };

  const app = nativePlugin('App');
  let resumeSub = null;
  if (app?.addListener) {
    // addListener may return a handle or a promise resolving to one.
    resumeSub = app.addListener('resume', tick);
  }
  const unsubscribePolicyReady = subscribeOtaPolicyReady(tick);
  const interval = setInterval(tick, intervalMs);

  tick(); // initial check on start

  stopScheduler = () => {
    clearInterval(interval);
    unsubscribePolicyReady();
    Promise.resolve(resumeSub)
      .then((sub) => sub?.remove?.())
      .catch(() => {});
    stopScheduler = null;
  };
  return stopScheduler;
};

// Boot both halves: signal ready (rollback safety) first, then start checks.
export const bootstrapOta = () => {
  notifyAppReady();
  startOtaUpdates();
};

// Test-only.
export const __resetScheduler = () => {
  if (stopScheduler) stopScheduler();
  stopScheduler = null;
};
